import type { FillMode } from '../../../video-data/types';
import { Config } from '../../../video-data/config';
import { calculateScaledResolution } from '../mediaUtils';

/**
 * How much of a media zoomed below a 1:1 fit must stay on screen when it is panned fully away.
 */
export const XY_TRANSLATION_MARGIN_PX = 10;

export type Size = { width: number; height: number };
export type Point = { x: number; y: number };
export type Rect = Point & Size;

/** How far the media is scaled from its fit. The half of a {@link Crop} the pan limit depends on. */
export type Zoom = { scaleX: number; scaleY: number };

/** The user's crop: a zoom, and a pan expressed as a fraction of the media's own fit box. */
export type Crop = Zoom & { tx: number; ty: number };

export type TranslationLimit = {
    /**
     *  txLimit - max value of transformMatrix.tx
     */
    x: [number, number];
    /**
     * tyLimit - max value of transformMatrix.ty
     */
    y: [number, number];
};

/**
 * Where the geometry reads its inputs from. Each is a function so a single long-lived instance
 * always sees current values, and so a getter only pays for what it actually reads — asking for
 * `displaySize` never touches the visible region.
 */
export type MediaGeometrySource = {
    /** The source's decoded resolution. */
    sourceResolution: () => Size;
    /** The box the source is scaled to fit. */
    fitBox: () => Size;
    /** The region the viewer actually sees the media in. */
    visibleRegion: () => Rect;
    fillMode: () => FillMode;
    crop: () => Crop;
};

/**
 * How big the media is and where it sits, in scene pixels. The single answer to those questions, so
 * that the size a container is given, the limits offered to the editor and the region chosen for
 * rasterising cannot disagree with each other.
 *
 * Holds no PIXI and no DOM. One instance per asset, living as long as the asset does: it reads its
 * inputs on demand rather than being handed a snapshot, so there is nothing to keep in sync.
 */
export class MediaGeometry {
    #source: MediaGeometrySource;

    constructor(source: MediaGeometrySource) {
        this.#source = source;
    }

    /**
     * The size the media is drawn at: the source fitted to the fit box by its fill mode, times the
     * zoom.
     */
    get displaySize(): Size {
        const fitted = calculateScaledResolution(
            this.#source.sourceResolution(),
            this.#source.fitBox(),
            this.#source.fillMode(),
        );
        const { scaleX, scaleY } = this.#source.crop();
        return { width: fitted.width * scaleX, height: fitted.height * scaleY };
    }

    /**
     * How far the user's pan moves the media from where its transformation places it. Reads the
     * visible region and the display size once: this runs per asset per frame, and a stencil-masked
     * media pays for a mask bounds calculation every time it is asked.
     */
    get panOffset(): Point {
        const cropTranslation = this.#cropTranslation(
            this.#source.visibleRegion(),
            this.displaySize,
        );
        return { x: -cropTranslation.x, y: -cropTranslation.y };
    }

    /**
     * {@link translationLimitFrom} for this media, at the precision the scene-design editor's
     * translate sliders step through.
     */
    get translationLimit(): TranslationLimit {
        const round = (limit: number) => parseFloat(limit.toFixed(2));
        const exact = MediaGeometry.translationLimitFrom(
            this.displaySize,
            this.#source.visibleRegion(),
            this.#source.crop(),
            this.#source.fitBox(),
        );
        return {
            x: [round(exact.x[0]), round(exact.x[1])],
            y: [round(exact.y[0]), round(exact.y[1])],
        };
    }

    /**
     * {@link translationLimit} for a media the geometry does not describe: sizes and a zoom in,
     * limit out. Static because the editor's crop gizmo has to know how far a zoom may pan while
     * the user is still dragging it, before anything is committed to an asset — an answer it would
     * otherwise have to arrive at with its own copy of this arithmetic.
     *
     * `displaySize` must already have the zoom applied, the way {@link displaySize} does. The zoom
     * is read only to decide whether the media has dropped below a 1:1 fit and so earns the
     * off-frame margin; it is never multiplied in. A caller that passes the unzoomed fit size
     * alongside the zoom gets a wrong limit rather than an error.
     *
     * The limit is how far the media may move within `visibleSize`, expressed as a fraction of
     * `panBox`, the media's own fit box that a pan is measured in. It defaults to `visibleSize`,
     * which is the same box whenever the media fills the region it is seen in.
     */
    static translationLimitFrom(
        displaySize: Size,
        visibleSize: Size,
        zoom: Zoom,
        panBox: Size = visibleSize,
    ): TranslationLimit {
        if (
            visibleSize.width <= 0 ||
            visibleSize.height <= 0 ||
            panBox.width <= 0 ||
            panBox.height <= 0
        ) {
            return { x: [0, 0], y: [0, 0] };
        }
        const axis = (objectSize: number, visible: number, pan: number) =>
            MediaGeometry.#maxOffset(objectSize, visible, zoom) / pan;
        const tx = axis(displaySize.width, visibleSize.width, panBox.width);
        const ty = axis(displaySize.height, visibleSize.height, panBox.height);
        return { x: [-tx, tx], y: [-ty, ty] };
    }

    /**
     * The zoom that makes the whole source visible inside the fit box: undo the fill mode's scale,
     * then apply a contain fit. Takes the declared source dimensions rather than the decoded ones,
     * which is the resolution this has always been computed from.
     */
    static scaleToContain(declaredResolution: Size, fitBox: Size, fillMode: FillMode): number {
        const { ratio } = calculateScaledResolution(declaredResolution, fitBox, fillMode);
        const containRatio = calculateScaledResolution(
            declaredResolution,
            fitBox,
            Config.FILL_CONTAIN,
        ).ratio;
        return (1 / ratio) * containRatio;
    }

    /**
     * How far the user's pan moves the media, clamped so it cannot be dragged further than it has
     * ground to give. A media zoomed below a 1:1 fit is allowed to leave the frame until only
     * {@link XY_TRANSLATION_MARGIN_PX} of it remains.
     *
     * The pan is a fraction of the media's own fit box rather than of the region it is seen in, so
     * resizing the mask that clips the media reveals or hides it without sliding it.
     */
    #cropTranslation(visibleRegion: Rect, displaySize: Size): Point {
        const crop = this.#source.crop();
        const fitBox = this.#source.fitBox();
        return {
            x: MediaGeometry.#axisTranslation(
                displaySize.width,
                visibleRegion.width,
                fitBox.width,
                crop.tx,
                crop,
            ),
            y: MediaGeometry.#axisTranslation(
                displaySize.height,
                visibleRegion.height,
                fitBox.height,
                crop.ty,
                crop,
            ),
        };
    }

    /**
     * How far off centre the media may sit on one axis. Half its overflow, plus — when it is zoomed
     * below a 1:1 fit and so has no overflow to give — almost its whole width. Shared, so the limit
     * offered to the editor and the clamp applied when the frame is drawn cannot drift apart.
     */
    static #maxOffset(objectSize: number, visibleSize: number, zoom: Zoom): number {
        const zoomIsLessThanOne = zoom.scaleX < 1.0 || zoom.scaleY < 1.0;
        const margin = zoomIsLessThanOne ? objectSize - XY_TRANSLATION_MARGIN_PX : 0;
        return Math.abs((objectSize - visibleSize) / 2) + margin;
    }

    static #axisTranslation(
        objectSize: number,
        visibleSize: number,
        panSize: number,
        translation: number,
        zoom: Zoom,
    ): number {
        const maxOffset = MediaGeometry.#maxOffset(objectSize, visibleSize, zoom);
        const userOffset = Math.abs(translation * panSize);
        const offset = Math.min(maxOffset, userOffset);
        return offset * (translation < 0 ? -1 : 1);
    }
}
